import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

console.info('+++++++++++++++ firebase config init +++++++++++++++');

// 카카오톡 인앱 브라우저 감지 함수
const isKakaoInAppBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.indexOf('kakaotalk') > -1;
};

// Service Worker 지원 여부 확인
const isServiceWorkerSupported = () => {
    return 'serviceWorker' in navigator;
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const realtimeDatabase = getDatabase(app);

// 조건부 messaging 초기화 (카카오톡 인앱 브라우저가 아닌 경우에만)
let messaging: any = null;
let messagingInitialized = false;
let messagingInitializationPromise: Promise<void> | null = null;

// messaging 초기화 상태 변화를 구독할 수 있는 콜백들
const messagingReadyCallbacks: Array<() => void> = [];

const initializeMessaging = async () => {
    if (messagingInitializationPromise) {
        return messagingInitializationPromise;
    }

    messagingInitializationPromise = (async () => {
        try {
            if (!isKakaoInAppBrowser() && isServiceWorkerSupported()) {
                const isMessagingSupported = await isSupported();
                if (isMessagingSupported) {
                    messaging = getMessaging(app);
                    messagingInitialized = true;
                    console.log('Firebase Messaging initialized successfully');

                    // 모든 대기 중인 콜백 실행
                    messagingReadyCallbacks.forEach((callback) => callback());
                    messagingReadyCallbacks.length = 0; // 배열 비우기
                } else {
                    console.warn('Firebase Messaging is not supported in this environment');
                    messagingInitialized = false;
                }
            } else {
                console.log('Firebase Messaging disabled in KakaoTalk in-app browser or no service worker support');
                messagingInitialized = false;
            }
        } catch (error) {
            console.error('Error initializing Firebase Messaging:', error);
            messagingInitialized = false;
        }
    })();

    return messagingInitializationPromise;
};

// Messaging 초기화 실행
initializeMessaging();

// Messaging 초기화 상태 확인 함수
const isMessagingReady = () => {
    return messagingInitialized && messaging !== null;
};

// Messaging이 준비될 때까지 기다리는 함수
const waitForMessaging = async (): Promise<any> => {
    if (isMessagingReady()) {
        return messaging;
    }

    // 카카오톡 인앱 브라우저나 Service Worker 미지원 환경이면 null 반환
    if (isKakaoInAppBrowser() || !isServiceWorkerSupported()) {
        return null;
    }

    // 초기화가 진행 중이면 기다림
    if (messagingInitializationPromise) {
        await messagingInitializationPromise;
        return messaging;
    }

    // 초기화가 시작되지 않았으면 Promise로 대기
    return new Promise((resolve) => {
        if (isMessagingReady()) {
            resolve(messaging);
        } else {
            messagingReadyCallbacks.push(() => resolve(messaging));
        }
    });
};
// const analytics = getAnalytics(app);
// const storage = getStorage(app);
// const firestore = getFirestore(app);
// const googleAuthProvider = new GoogleAuthProvider();

const getPermission = async () => {
    try {
        // 카카오톡 인앱 브라우저에서는 알림 권한 요청하지 않음
        if (isKakaoInAppBrowser()) {
            console.log('Notification permission skipped in KakaoTalk in-app browser');
            return 'KakaoTalk in-app browser - notifications not supported';
        }

        // messaging이 초기화되지 않은 경우
        if (!isMessagingReady()) {
            console.warn('Firebase Messaging not ready');
            return 'Firebase Messaging not available';
        }

        // Service Worker 지원 확인
        if (!isServiceWorkerSupported()) {
            console.warn('Service Worker not supported');
            return 'Service Worker not supported';
        }

        // Request permission for notifications
        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
            console.log('Notification permission granted');

            try {
                const token = await getToken(messaging, { vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY });
                // console.log(token, 'token');
                return token;
            } catch (tokenError) {
                console.error('Error getting FCM token:', tokenError);
                return 'Error getting FCM token';
            }
        } else {
            console.log('Notification permission denied');
            return 'Notification permission denied';
        }
    } catch (error) {
        console.error('Error setting up notifications:', error);
        return 'Error setting up notifications';
    }
};

export { realtimeDatabase, messaging, getPermission, auth, isMessagingReady, waitForMessaging };
